import React from 'react';

import { Identify, identify, init, setUserId } from '@amplitude/analytics-browser';
import { datadogRum } from '@datadog/browser-rum';
import * as Sentry from '@sentry/react';
import Cookies from 'js-cookie';

import logger from 'src/clients/LoggingClient';
import { ApiEnv } from 'src/clients/Network/http/fetch';
import { CkSubIdCookieKey, X1InviteCookieKey } from 'src/components/creditApplication/NavigationProvider';
import { useGlobalStore } from 'src/state/global';

const useAmplitude = (): void => {
  const identityId = useGlobalStore((s) => s.identityId);
  // initialize Amplitude
  React.useEffect(() => {
    (async () => {
      try {
        Sentry.addBreadcrumb({
          message: 'Initialize Amplitude',
          level: 'debug',
        });

        const apiKey =
          ApiEnv === 'production' ? 'bfad3b4bcece0191d29eb95abb17e1cc' : 'b8672f3e6f94f0c5d2698a89171f75a4';

        // NOTE: minIdLength is explicitly defined as 1 to prevent Amplitude throwing errors
        // for going below their default min id length of 5
        // https://www.docs.developers.amplitude.com/analytics/apis/http-v2-api/?h=min#device-ids-and-user-ids-minimum-length
        await init(apiKey, undefined, {
          minIdLength: 1,
          defaultTracking: {
            attribution: true, // referrers, UTM, click ids
            sessions: false,
            pageViews: false,
            fileDownloads: false,
            formInteractions: false,
          },
        }).promise;

        const identifyObj = new Identify();
        identifyObj.set(
          'channel',
          Cookies.get(CkSubIdCookieKey) != null ? 'ck' : Cookies.get(X1InviteCookieKey) != null ? 'x1' : 'unknown',
        );
        identify(identifyObj);
      } catch (e) {
        logger.error(e, { source: 'useAmplitude' });
      }
    })();
  }, []);

  // set user id
  React.useEffect(() => {
    (async () => {
      if (!!identityId) {
        try {
          Sentry.addBreadcrumb({
            message: 'Set Amplitude & Sentry to stored identity id',
            level: 'debug',
            data: { identityId },
          });
          Sentry.setUser({
            id: identityId,
          });
          datadogRum.setUser({ id: identityId });
          setUserId(identityId);
        } catch (e) {
          logger.error(e, { source: 'useAmplitude.setUserId' });
        }
      }
    })();
  }, [identityId]);
};

export default useAmplitude;
